import React, { useState, useRef, useEffect } from "react";
import styled, { createGlobalStyle, css } from "styled-components";
import firebase from "gatsby-plugin-firebase";

import Layout from "../layouts/main-layout";
import OutlinedTitle from "../components/titles/outlined";
import Card from "../components/card";
import LinkButton from "../components/link-button";
import AppWindow from "../components/window-styled-container";

import mappingProdiFakultasDapil from "../mappings/fakultas-prodi.json";

const GlobalStyle = createGlobalStyle`
    body {
        background-color: var(--kpum-cream);
    }
`;

const MainContainer = styled.div`
    padding: 4rem 2rem;

    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
`;

const PageTitle = styled(OutlinedTitle)`
    font-size: 3em;
`;

const Form = styled.form`
    display: block;

    & > label {
        display: block;

        font-size: 1em;
        font-weight: bold;
        color: var(--kpum-black);
    }

    & > button {
        display: block;

        margin: 0 auto;
        margin-top: 1em;

        font-family: unset; /* or inherit? */
        font-size: 0.95em;

        cursor: pointer;
    }
`;

const InputBox = styled(Card).attrs({ as: "input" })`
    border: none; /* ? */
    padding: 1em;
    margin: 0.75em auto;

    width: 10em;

    font-family: inherit;

    /* Take initial shadow value from the main Card component */
    &:focus {
        /* Reset user-agent focus ring style */
        outline: none;

        box-shadow:
            0 0 0 0.2em var(--kpum-lightorange),
            0 2px 5px rgb(0 0 0 / 0.5);
    }

    /* Disable default number input appearence */
    appearance: textfield;

    &::-webkit-inner-spin-button, &::-webkit-outer-spin-button {
        -webkit-appearance: none;
    }
`;

const ResultContainer = styled.section`
    width: 100%;

    animation: height-expand 1s;

    @keyframes height-expand {
        from { max-height: 0; }
        to { max-height: 100vh; }
    }
`;

const ResultWindow = styled(AppWindow)`
    width: fit-content;
    padding: 2em;

    p {
        /* Some resets */
        margin: 0;
    }

    animation: resultwindow-appear-scale 0.5s 0.5s backwards;

    @keyframes resultwindow-appear-scale {
        from {
            transform: scale(0.5);
            opacity: 0;
        }
    }
`;

const ResultGreeter = styled.p`
    font-size: 1.75em;
    font-weight: bold;
    font-family: var(--qanelas-main);

    padding: 0 0.5em;

    &.rg-color-neutral {
        color: var(--kpum-black);
    }
    &.rg-color-warning {
        color: var(--ugm-red);
    }
    &.rg-color-success {
        color: var(--kpum-darkteal);
    }
`;

const tableRowBorderRadius = `0.5rem`;

const ResultTable = styled.table`
    text-align: left;
    border-spacing: 0 0.5em;
    margin: 0 auto;
    margin-top: 1em;

    tr {
        border-radius: 10px;
    }

    td {
        background-color: rgb(0 0 0 / 0.1);
        padding: 1em;

        &:first-child {
            border-radius: ${tableRowBorderRadius} 0 0 ${tableRowBorderRadius};
        }

        &:last-child {
            border-radius: 0 ${tableRowBorderRadius} ${tableRowBorderRadius} 0;

            font-weight: bold;
        }
    }
`;

// Firebase thingy below

const firebaseInstance = firebase.database?.().ref();

// This thing returns a promise
const asyncQueryData = niuQuery => firebaseInstance?.orderByChild("NIU").equalTo(niuQuery).limitToFirst(1).once("value");

const DPTPage = ({ location }) => {
    const [pemilihData, setPemilihData] = useState(null);

    const niuInputRef = useRef(null);

    function processData(niuData, fakultasData) {
        if (niuData === "" || niuData === null) {
            setPemilihData({
                error: "input-salah"
            });
        }
        else {
            setPemilihData({
                loading: true
            });

            // niuData masih dalam bentuk string, jadi convert dulu ke integer
            let niuQuery = parseInt(niuData);

            asyncQueryData(niuQuery).then(snapshot => {
                console.log(snapshot.val());

                if (snapshot.val() === null) {
                    setPemilihData({
                        error: "tidak-terdaftar"
                    })
                } else {
                    let returnedData = Object.values(snapshot.val())[0];
                    setPemilihData({
                        nama: returnedData.Nama,
                        niu: returnedData.NIU,
                        prodi: returnedData.Prodi,
                        angkatan: returnedData.Angkatan
                    });
                }
            });
        }
    }

    function handleSubmit(e) {
        e.preventDefault();
        processData(niuInputRef.current.value);
    }

    useEffect(() => {
        if (location.state?.preenteredNiuValue) {
            niuInputRef.current.value = location.state.preenteredNiuValue;
            processData(location.state.preenteredNiuValue);
        }
    }, []); // The empty array is so that the useEffect is run only once

    const ConditionalResultGreeter = () => {
        if (pemilihData) {
            if (pemilihData.loading) {
                return (<ResultGreeter className="rg-color-neutral">Sedang memuat, mohon tunggu sebentar.</ResultGreeter>);
            }
            else if (pemilihData.error === "input-salah") {
                return (<ResultGreeter className="rg-color-warning">Mohon periksa kembali masukan NIU di atas.</ResultGreeter>);
            }
            else if (pemilihData.error === "tidak-terdaftar") {
                return (<ResultGreeter className="rg-color-warning">Maaf, NIU yang dicari tidak terdaftar di DPT.</ResultGreeter>);
            }
            else if (pemilihData.niu && pemilihData.prodi) {
                return (<ResultGreeter className="rg-color-success">Selamat, Anda terdaftar di DPT!</ResultGreeter>);
            }
            else {
                // Just in case of some very rare cases
                return (<ResultGreeter>Terjadi eror</ResultGreeter>);
            }
        }
        else {
            return null;
        }
    }

    return (
        <Layout title="Cek Status Kepemilihan">
            <GlobalStyle />
            <MainContainer>
                <PageTitle>Cek status Daftar Pemilih Tetap</PageTitle>
                <Form onSubmit={handleSubmit}>
                    <label htmlFor="NIU">Masukkan NIU Anda:</label>
                    <InputBox type="number" name="NIU" ref={niuInputRef} tabIndex="1" placeholder="6 digit" min="100000" max="999999" autoFocus />
                    <LinkButton as="button">Cek</LinkButton>
                </Form>
                <ResultContainer style={pemilihData ? null : { display: `none` }}>
                    <ResultWindow>
                        <ConditionalResultGreeter />
                        {(pemilihData && pemilihData.niu && pemilihData.prodi) ? (
                            <ResultTable>
                                <tbody>
                                    <tr>
                                        <td>Nama</td>
                                        <td>{pemilihData.nama}</td>
                                    </tr>
                                    <tr>
                                        <td>NIU</td>
                                        <td>{pemilihData.niu}</td>
                                    </tr>
                                    <tr>
                                        <td>Angkatan</td>
                                        <td>{pemilihData.angkatan}</td>
                                    </tr>
                                    <tr>
                                        <td>Program Studi</td>
                                        <td>{pemilihData.prodi}</td>
                                    </tr>
                                    <tr>
                                        <td>Fakultas/Sekolah</td>
                                        <td>{mappingProdiFakultasDapil[pemilihData.prodi]["Fakultas"]}</td>
                                    </tr>
                                    <tr>
                                        <td>Daerah Pemilihan</td>
                                        <td>{mappingProdiFakultasDapil[pemilihData.prodi]["Dapil"]}</td>
                                    </tr>
                                </tbody>
                            </ResultTable>
                        ) : null}
                    </ResultWindow>
                </ResultContainer>
            </MainContainer>
        </Layout>
    )
};

export default DPTPage;